import type { ErrorView } from '@krp/client-runtime'

/**
 * Модель раздела «График выхода» — слова договора службы `schedule-api` (`src/Schedule/README.md`). График — это «объект → состав →
 * день → кассиры». Служба знает о кассире только его идентификатор: имя берёт клиент из справочника кассиров (`CashierDirectory`).
 */

/** Календарный день `ГГГГ-ММ-ДД` без часового пояса: день графика — календарный день точки. */
export type IsoDay = string

export interface ScheduleAccess {
  canView: boolean
  canManage: boolean
}

/** Объект графика: магазин, точка, любое место с вольным названием («Волная 28»). */
export interface Place {
  id: string
  name: string
  sortOrder: number
  archived: boolean
}

/** Вид отметки в клетке: «Р» выходит, «В» выходной день, «Н» не выходит (явная отметка). Записи нет — клетка не отмечена. */
export type AssignmentKind = 'work' | 'day_off' | 'absent'

/** Все виды отметки: из них декодер принимает значение, неизвестное — ошибка разбора. */
export const ASSIGNMENT_KINDS: readonly AssignmentKind[] = ['work', 'day_off', 'absent']

/** Отметка кассира на объекте в день. */
export interface Assignment {
  id: string
  placeId: string
  cashierId: string
  date: IsoDay
  kind: AssignmentKind
  updatedAt: string
  updatedBy: string
}

export interface PlacesArgs {
  includeArchived: boolean
}

/** Назначения по всем объектам за период (включительно). */
export interface AllAssignmentsRange {
  from: IsoDay
  to: IsoDay
}

export interface CreatePlaceArgs {
  name: string
}

export interface UpdatePlaceArgs {
  id: string
  name: string
  archived: boolean
}

/** Один кассир в один день объекта: поставить отметку или очистить клетку (точечная команда). */
export interface CashierDayArgs {
  placeId: string
  date: IsoDay
  cashierId: string
}

/** Поставить отметку: без `kind` — «выходит». */
export interface CashierDaySetArgs extends CashierDayArgs {
  kind?: AssignmentKind
}

/** Кассир в постоянном составе объекта: его строка в таблице объекта. */
export interface Member {
  cashierId: string
  addedAt: string
}

export interface MembersArgs {
  placeId: string
}

/** Один кассир в составе одного объекта: добавить или убрать. */
export interface MemberArgs {
  placeId: string
  cashierId: string
}

/** Снятие кассира с объекта: назначения с даты `clearFrom` включительно снимаются вместе с записью состава, прошлые остаются. */
export interface MemberRemoveArgs extends MemberArgs {
  clearFrom: IsoDay
}

/** Кассир из справочника: идентификатор и имя. */
export interface CashierPerson {
  id: string
  name: string
}

/**
 * Порт справочника кассиров (реализует приложение в `bindings/schedule`). Служба графика имён не хранит: клиент подписывает
 * кассиров и даёт выбрать их отсюда. `unavailable` — справочник не прочитался; причина показывается дословно.
 */
export interface CashierDirectory {
  status: 'loading' | 'ready' | 'unavailable'
  error: ErrorView | null
  /** Кого можно назначить на день (без архивных). */
  people: readonly CashierPerson[]
  nameOf: (cashierId: string) => string
}

/** Отказ запроса или команды: заголовок — что не вышло; текст, код и номер запроса — как пришли от службы. */
export interface ShownError {
  title: string
  error: ErrorView
}

/**
 * Отказ «день занят» у клетки: не ошибка экрана, а вспышка клетки с подсказкой рядом. `seq` растёт при каждом отказе подряд —
 * по его чётности клетка перезапускает анимацию без пересоздания кнопки (фокус остаётся).
 */
export interface DeniedCell {
  /** Ключ клетки (`cellKey`). */
  key: string
  /** Текст подсказки: для занятого дня, известного клиенту, — «Нельзя: день занят — объект «…»», для отказа службы — её текст дословно. */
  text: string
  seq: number
}

export const PLACE_NAME_MAX = 100
